import type {RefractoryBulkUpdateRowValidationResult} from '@domain/refractory-product-catalog'
import {
  clx,
  Pill,
  TableBase,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
  type PillType
} from '@hm/fundamental'
import {useTranslation} from 'react-i18next'

import {FEATURE_NAMESPACE} from '../../consts'

interface RefraCatalogUploadPreviewTableProps {
  rows: RefractoryBulkUpdateRowValidationResult[]
}

const headerCellClassName = '!table-cell !rounded-none whitespace-nowrap py-2 text-foreground_faded'
const cellClassName =
  '!table-cell !border-b border-border !py-4 align-top [&>div:first-child]:hidden'
const statusPillTypes: Record<string, PillType> = {
  New: 'positive',
  Updated: 'positive',
  Mothballed: 'warning'
}

export const RefraCatalogUploadPreviewTable: React.FC<RefraCatalogUploadPreviewTableProps> = ({
  rows
}) => {
  const {t} = useTranslation(FEATURE_NAMESPACE)

  return (
    <div className="max-h-[28rem] overflow-auto" data-cy="refra-catalog-upload-preview">
      <TableBase className="!table min-w-[48rem] table-fixed text-16px leading-6 text-foreground">
        <colgroup>
          <col className="w-1/4" />
          <col className="w-1/4" />
          <col className="w-[12%]" />
          <col className="w-[38%]" />
        </colgroup>
        <TableHeader className="sticky top-0 z-10 !table-header-group [&>tr]:!not-sr-only">
          <TableHeaderCell className={headerCellClassName}>
            {t('refraCatalog.table.supplier')}
          </TableHeaderCell>
          <TableHeaderCell className={headerCellClassName}>
            {t('refraCatalog.table.brandName')}
          </TableHeaderCell>
          <TableHeaderCell className={headerCellClassName}>
            {t('refraCatalog.table.revision')}
          </TableHeaderCell>
          <TableHeaderCell className={headerCellClassName}>
            {t('refraCatalog.table.statusChange')}
          </TableHeaderCell>
        </TableHeader>
        <TableBody className="!table-row-group">
          {rows.map((row, index) => {
            const hasErrors = row.errors.length > 0

            return (
              <TableRow
                key={`${index}-${row.name}-${row.revision}`}
                className={clx('!table-row', hasErrors && 'bg-negative_faded_10 text-negative')}
                data-cy={`refra-catalog-upload-preview-row-${index}`}
              >
                <TableCell className={cellClassName}>
                  <span className="break-words">{row.manufacturerName}</span>
                </TableCell>
                <TableCell className={cellClassName}>
                  <span className="break-words">{row.name}</span>
                </TableCell>
                <TableCell className={cellClassName}>{row.revision || '—'}</TableCell>
                <TableCell className={cellClassName}>
                  <div className="flex flex-col items-start gap-2">
                    <Pill
                      type={hasErrors ? 'negative' : (statusPillTypes[row.status] ?? 'neutral')}
                      text={t(`refraCatalog.upload.status.${row.status}`, {
                        defaultValue: row.status
                      })}
                    />
                    {row.errors.map((error, errorIndex) => (
                      <span
                        key={`${errorIndex}-${error}`}
                        role="alert"
                        className="break-words text-14px leading-5 text-negative"
                      >
                        {error}
                      </span>
                    ))}
                  </div>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </TableBase>
    </div>
  )
}
